<template>
	<div class="w-full max-w-4xl mx-auto">
		<!-- Header Card -->
		<div class="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
			<div class="h-2 bg-primary"></div>

			<div class="p-8">
				<div class="flex items-start justify-between gap-4">
					<div class="flex items-start gap-4 flex-1">
						<div
							class="w-14 h-14 rounded-xl bg-primary text-white flex items-center justify-center font-bold text-xl shadow-md flex-shrink-0"
						>
							{{ valuerInitials }}
						</div>
						<div>
							<h2
								class="text-2xl font-bold text-text-primary mb-1"
							>
								{{ valuer.name }}
							</h2>
							<p
								class="text-sm text-text-secondary flex items-center gap-2"
							>
								<svg
									class="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									></path>
								</svg>
								Complete Valuation Summary
							</p>
						</div>
					</div>

					<button
						@click="$emit('close')"
						class="w-10 h-10 rounded-xl bg-surface hover:bg-border text-text-secondary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 flex items-center justify-center flex-shrink-0"
						aria-label="Close summary"
					>
						<svg
							class="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							></path>
						</svg>
					</button>
				</div>
			</div>
		</div>

		<!-- Timeline Card -->
		<div class="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
			<div class="p-8">
				<div class="flex items-center gap-3 mb-8">
					<div
						class="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center"
					>
						<svg
							class="w-5 h-5 text-accent"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
							></path>
						</svg>
					</div>
					<div>
						<h3 class="text-lg font-bold text-text-primary">
							Activity Timeline
						</h3>
						<p class="text-sm text-text-secondary">
							Detailed chronology of valuation stages
						</p>
					</div>
				</div>

				<div class="relative">
					<!-- Vertical line -->
					<div
						class="absolute left-7 top-0 bottom-0 w-0.5 bg-border"
					></div>

					<div class="space-y-6">
						<div
							v-for="(item, idx) in timeline"
							:key="item.key"
							class="relative flex items-start gap-6"
						>
							<!-- Timeline dot -->
							<div class="relative z-10 flex-shrink-0">
								<div
									class="w-14 h-14 rounded-xl flex items-center justify-center shadow-md transition-all duration-300"
									:class="
										item.completed
											? 'bg-primary text-white'
											: 'bg-surface text-text-secondary border-2 border-border'
									"
								>
									<svg
										v-if="item.completed"
										class="w-6 h-6"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="3"
											d="M5 13l4 4L19 7"
										></path>
									</svg>
									<svg
										v-else
										class="w-6 h-6"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
										></path>
									</svg>
								</div>
							</div>

							<!-- Timeline content -->
							<div class="flex-1 pb-6">
								<div
									class="bg-surface border rounded-xl p-6 transition-all duration-300 hover:shadow-md"
									:class="
										item.completed
											? 'border-primary/20 bg-primary/5'
											: 'border-border'
									"
								>
									<div
										class="flex items-start justify-between gap-4 mb-3"
									>
										<div>
											<h4
												class="text-lg font-bold text-text-primary mb-1"
											>
												{{ item.label }}
											</h4>
											<span
												class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full"
												:class="
													item.completed
														? 'bg-primary/10 text-primary'
														: 'bg-surface text-text-secondary border border-border'
												"
											>
												<div
													class="w-1.5 h-1.5 rounded-full"
													:class="
														item.completed
															? 'bg-primary'
															: 'bg-text-secondary'
													"
												></div>
												{{
													item.completed
														? 'Completed'
														: 'Pending'
												}}
											</span>
										</div>

										<div
											class="flex items-center gap-2 text-sm font-semibold text-text-secondary bg-white border border-border px-3 py-2 rounded-lg"
										>
											<svg
												class="w-4 h-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
												></path>
											</svg>
											{{
												item.date
													? formatDate(item.date)
													: 'Not recorded'
											}}
										</div>
									</div>

									<div
										v-if="item.note"
										class="bg-white border border-border rounded-lg p-4"
									>
										<div class="flex items-start gap-2">
											<svg
												class="w-4 h-4 text-text-secondary mt-0.5 flex-shrink-0"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
												></path>
											</svg>
											<p
												class="text-sm text-text-primary leading-relaxed"
											>
												{{ item.note }}
											</p>
										</div>
									</div>
									<div
										v-else
										class="text-sm text-text-secondary italic"
									>
										No additional details recorded for this
										stage.
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Overall Summary Card -->
		<div class="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
			<div class="p-8">
				<div class="flex items-start gap-3 mb-6">
					<div
						class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"
					>
						<svg
							class="w-5 h-5 text-primary"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							></path>
						</svg>
					</div>
					<div>
						<h3 class="text-lg font-bold text-text-primary mb-1">
							Overall Summary
						</h3>
						<p class="text-sm text-text-secondary">
							Comprehensive overview of the valuation
						</p>
					</div>
				</div>

				<div class="bg-surface border border-border rounded-xl p-6">
					<p class="text-sm text-text-primary leading-relaxed">
						{{
							valuer.summary ||
							'No summary provided for this valuation.'
						}}
					</p>
				</div>
			</div>
		</div>

		<!-- Action Buttons -->
		<div class="bg-white rounded-2xl shadow-lg overflow-hidden">
			<div class="p-6">
				<div class="flex items-center justify-between gap-4">
					<button
						@click="$emit('close')"
						class="px-6 py-3 text-sm font-bold bg-surface text-text-primary rounded-xl hover:bg-border border-2 border-border hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 active:scale-95"
						aria-label="Close summary"
					>
						Close
					</button>

					<button
						@click="downloadSummary"
						class="flex-1 max-w-xs px-6 py-3 text-sm font-bold bg-accent hover:bg-accent-light text-white rounded-xl shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-all duration-200 active:scale-95 flex items-center justify-center gap-2"
						aria-label="Download summary"
					>
						<svg
							class="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							></path>
						</svg>
						Download Summary
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ValuationSummary',
	props: {
		valuer: {
			type: Object,
			required: true,
		},
	},
	computed: {
		valuerInitials() {
			if (!this.valuer || !this.valuer.name) return '';
			const parts = this.valuer.name.split(' ').filter(Boolean);
			let first = '';
			let second = '';
			if (parts.length > 0 && parts[0] && parts[0].length > 0)
				first = parts[0][0];
			if (parts.length > 1 && parts[1] && parts[1].length > 0)
				second = parts[1][0];
			return (first + second).toUpperCase();
		},
		timeline() {
			const entries = [
				{
					key: 'started',
					label: 'Started',
					date: this.valuer.startedDate || '',
					note: this.valuer.startedNote || '',
				},
				{
					key: 'processing',
					label: 'Processing',
					date: this.valuer.processingDate || '',
					note: this.valuer.processingNote || '',
				},
				{
					key: 'approved',
					label: 'Approved',
					date: this.valuer.approvedDate || '',
					note: this.valuer.approvedNote || '',
				},
				{
					key: 'completed',
					label: 'Completed',
					date: this.valuer.endDate || '',
					note: this.valuer.completedNote || '',
				},
			];
			return entries.map((e) => ({ ...e, completed: !!e.date }));
		},
	},
	methods: {
		formatDate(d) {
			if (!d) return '';
			try {
				const date = new Date(d);
				return date.toLocaleDateString('en-US', {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
				});
			} catch (e) {
				return d;
			}
		},
		downloadSummary() {
			const lines = [];
			lines.push(`${this.valuer.name} - Valuation Summary`);
			lines.push('='.repeat(50));
			lines.push('');
			lines.push('ACTIVITY TIMELINE');
			lines.push('-'.repeat(50));
			this.timeline.forEach((t) => {
				lines.push('');
				lines.push(
					`${t.label}: ${t.date ? this.formatDate(t.date) : 'Not recorded'}`,
				);
				lines.push(`Status: ${t.completed ? 'Completed' : 'Pending'}`);
				if (t.note) {
					lines.push(`Note: ${t.note}`);
				}
			});
			lines.push('');
			lines.push('='.repeat(50));
			lines.push('OVERALL SUMMARY');
			lines.push('-'.repeat(50));
			lines.push(this.valuer.summary || 'No summary provided.');
			lines.push('');
			lines.push('='.repeat(50));
			lines.push(`Generated: ${new Date().toLocaleString()}`);

			const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${this.valuer.id || 'valuation'}-summary.txt`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
		},
	},
};
</script>

<style scoped>
/* Minimal custom styles - Tailwind handles most styling */
</style>
